<template>
  <v-dialog
    v-model="dialog"
    ref="dialog"
    scrollable
    max-width="1200px"
    @click:outside="close"
    @keydown="close"
  >
    <v-card>
      <v-card-title>
        {{ $t("contributions.information.title") }}
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text style="height: 800px;">
        <v-row no-gutters>
          <v-col v-for="item in items" :key="item.title" class="mt-2">
            <ListDialogTable :item="item"></ListDialogTable>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider></v-divider>
      <RawVisualization
        v-if="item"
        :server-url="endpoint.address"
        :study-uid="item.identifier[0].value.substr(8)"
        :series-uid="item.series[0].uid"
        :height="500"
      ></RawVisualization>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer />
        <v-btn color="blue darken-1" text @click="close">
          {{ $t("button.close") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import RawVisualization from "@/components/slide/RawVisualization";
import { getBySystem, getByUrl } from "@/utils/fhir-utils.js";
import { formatDate } from "@/filters";
import { fetchResource } from "@/api/fhir";
import ListDialogTable from "@/components/contributor/ListDialogTable";
import { DateTime } from "luxon";

export default {
  components: {
    RawVisualization,
    ListDialogTable
  },
  props: {
    show: Boolean,
    item: Object,
    endpoint: Object
  },
  data() {
    return {
      dialog: false,
      patient: null,
      specimen: null,
      items: []
    };
  },
  watch: {
    show: {
      immediate: true,
      handler(val) {
        this.dialog = val;
      }
    }
  },
  methods: {
    getMagnifications() {
      return this.item.extension
        .filter(
          e =>
            e.url ===
            "http://deeppath.cl/fhir/StructureDefinition/MagnificationExt"
        )
        .map(e => e.extension);
    },
    getPatientItems() {
      // Placeholder
      let patientItems = [
        {
          name: this.$t("contributions.information.patient.pseudonym"),
          value: Math.random()
            .toString(36)
            .substring(7)
            .concat("...")
        }
      ];
      try {
        patientItems = [
          {
            name: this.$t("contributions.information.patient.pseudonym"),
            value: getBySystem(
              this.patient.identifier,
              "http://deeppat.cl/fhir/patient-pseudonym"
            )["value"]
          }
        ];
      } catch (e) {
        console.log(e);
      }
      return patientItems;
    },
    getSpecimenItems() {
      return [
        {
          name: this.$t("contributions.information.specimen.procedure"),
          value: this.specimen.processing[0].procedure.coding[0].code
        },
        {
          name: this.$t("contributions.information.specimen.bodySite"),
          value: this.specimen.collection.bodySite.coding[0].code
        },
        {
          name: this.$t("contributions.information.specimen.staining"),
          value: this.specimen.processing[0].description
        },
        {
          name: this.$t("contributions.information.specimen.tissueType"),
          value: this.specimen.type.coding
            .map(coding => coding["code"])
            .join(" ")
        },
        {
          name: this.$t("contributions.information.specimen.collectionMethod"),
          value: this.specimen.collection.method.coding[0].code
        },
        {
          name: this.$t("contributions.information.specimen.collectionDate"),
          value: formatDate(
            this.specimen.collection.collectedDateTime,
            DateTime.DATE_SHORT
          )
        }
      ];
    },
    getImageItems() {
      const magnifications = this.getMagnifications();
      return [
        {
          name: this.$t("contributions.information.image.modality"),
          value: getBySystem(
            this.item.modality,
            "http://dicom.nema.org/resources/ontology/DCM"
          )["display"]
        },
        {
          name: this.$t("contributions.information.image.manufacturer"),
          value: getBySystem(
            this.item.modality,
            "http://deeppath.scian.cl/code-system/modality-manufacturer"
          )["display"]
        },
        {
          name: this.$t("contributions.information.image.model"),
          value: getBySystem(
            this.item.modality,
            "http://deeppath.scian.cl/code-system/modality-model"
          )["display"]
        },
        {
          name: this.$t("contributions.information.image.magnifications"),
          value: magnifications
            .map(
              magnification =>
                "x" + getByUrl(magnification, "magnification")["valueInteger"]
            )
            .join(" ")
        },
        {
          name: this.$t("contributions.information.image.pixelDepth"),
          value: `${
            getByUrl(
              this.item.extension,
              "http://deeppath.cl/fhir/StructureDefinition/PixelDepthExt"
            )["valueCode"]
          } bit`
        },
        {
          name: this.$t("contributions.information.image.studyID"),
          value: this.item.identifier[0].value.substring(8)
        },
        {
          name: this.$t("contributions.information.image.seriesID"),
          value: this.item.series[0].uid
        },
        {
          name: this.$t("contributions.information.image.creationDate"),
          value: formatDate(this.item.started, DateTime.DATE_SHORT)
        },
        {
          name: this.$t("contributions.information.image.contributionDate"),
          value: formatDate(
            getByUrl(
              this.item.extension,
              "http://deeppath.cl/fhir/StructureDefinition/DateOfContributionExt"
            )["valueDateTime"]
          )
        }
      ];
    },
    getItems() {
      this.items = [
        {
          title: this.$t("contributions.information.patient.title"),
          list: this.getPatientItems()
        },
        {
          title: this.$t("contributions.information.image.title"),
          list: this.getImageItems()
        },
        {
          title: this.$t("contributions.information.specimen.title"),
          list: this.getSpecimenItems()
        }
      ];
    },
    close() {
      this.$emit("input", false);
    }
  },
  async mounted() {
    try {
      this.patient = this.item.patientResource;
      this.specimen = this.item.specimenResource;
      this.getItems();
    } catch (e) {
      console.log(e);
    }
  }
};
</script>
<style scoped></style>
